import { logger } from '@springtree/eva-sdk-core-logger';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import getEvaEndpoint from '../helpers/get-eva-endpoint.js';
import currentUserTokenState from '../state/current-user/current-user-token-state.js';
import evaEndpointUrlState from '../state/core/eva-endpoint-url-state.js';
import requestedOrganizationUnitIdState from '../state/core/requested-organization-unit-id-state.js';
import requestedOrganizationUnitQueryState from '../state/core/requested-organization-unit-query-state.js';
import shoppingCartState from '../state/checkout/shopping-cart-state.js';
import useGetState from './use-get-state.js';
import storage from '../helpers/storage.js';
export const useShoppingCartModify = ({ service, options = {}, }) => {
    const evaEndpointUrl = useGetState(evaEndpointUrlState);
    const currentUserToken = useGetState(currentUserTokenState);
    const evaRequestedOrganizationUnitID = useGetState(requestedOrganizationUnitIdState);
    const evaRequestedOrganizationUnitQuery = useGetState(requestedOrganizationUnitQueryState);
    const shoppingCart = useGetState(shoppingCartState.response);
    const shoppingCartRequest = useGetState(shoppingCartState.request);
    const setCurrentUserToken = useSetRecoilState(currentUserTokenState);
    const setShoppingCartRequest = useSetRecoilState(shoppingCartState.request);
    const setShoppingCartStale = useSetRecoilState(shoppingCartState.stale);
    const modifyCart = useCallback(async ({ payload, callOptions, hookCallOptions, }) => {
        try {
            if (!evaEndpointUrl) {
                return;
            }
            const evaEndpoint = await getEvaEndpoint(evaEndpointUrl);
            // Ensure we have a valid order
            //
            const orderId = shoppingCart?.ShoppingCart.ID ?? shoppingCartRequest?.OrderID;
            if (!orderId) {
                logger.warn(`There is no active shopping cart to call ${service.name} on`)();
                if (options.onError) {
                    options.onError(new Error(`There is no active shopping cart to call ${service.name} on`));
                }
                return;
            }
            const requestPayload = {
                ...payload,
            };
            // Get the target service name. We must use an instance because the
            // name property on the prototype will change by the code bundler
            //
            const serviceName = new service().name;
            // If this is a create customer request we have to check a few things:
            //
            // * there is no current customer assigned to the cart
            // * auto login is enabled on the register call
            //
            if (serviceName === 'Core:CreateCustomer') {
                if (shoppingCart?.ShoppingCart?.Customer?.ID) {
                    throw new Error('The current order already has an attached customer');
                }
                if (!payload.AutoLogin) {
                    throw new Error('Auto-login must be enabled to attach the customer to the order');
                }
            }
            // If this is a login request we have to check a few things:
            //
            // * there is nu current customer assigned to the cart
            //
            if (serviceName === 'Core:Login') {
                if (shoppingCart?.ShoppingCart?.Customer?.ID) {
                    throw new Error('The current order already has an attached customer');
                }
            }
            // Certain services require the OrderID in their payload
            // You have to instantiate the service to get the name value
            // Using it static only works before bundling/transpiling
            // Learned that the hard way
            //
            const servicesRequiringOrderID = [
                'Core:AttachCustomerToOrder',
                'Core:AddDiscountCouponToOrder',
                'Core:CancelOrder',
                'Core:ChangeOrderLinesToCarryOut',
                'Core:ChangeOrderLinesToDelivery',
                'Core:ChangeOrderLinesToPickup',
                'Core:CreateCustomer',
                'Core:DetachCustomerFromOrder',
                'Core:PrepareOrderForCheckout',
                'Core:SetGiftWrappingOptionsOnOrder',
                'Core:SetRequestedDate',
                'Core:UpdateOrderAddresses',
                'Core:UpdateOrderBillingAddress',
                'Core:UpdateOrderCustomFields',
                'Core:UpdateOrderShippingAddress',
            ];
            if (servicesRequiringOrderID.indexOf(serviceName) !== -1) {
                requestPayload.OrderID = orderId;
            }
            // Options are combined in such a way that the caller can override
            // the token if desired
            //
            const response = await evaEndpoint.callService(service, requestPayload, {
                authenticationToken: currentUserToken,
                requestedOrganizationUnitID: evaRequestedOrganizationUnitID,
                requestedOrganizationUnitQuery: evaRequestedOrganizationUnitQuery,
                ...callOptions,
            });
            // The wrap-up for create customer and login is slightly different
            // The new user token must be set on the react context and the current
            // stored anonymous cart state needs to be migrated.
            // We will unset and reset the storage key values if present so that when
            // the context re-initializes it will retrieve the correct initial atom
            // values from storage
            //
            if (serviceName === 'Core:CreateCustomer' || serviceName === 'Core:Login') {
                const storageKey = `${evaEndpointUrl.replace(/https?:\/\//, '')}:default:shoppingCartDetails`;
                const storedCurrentOrderValue = storage.getItem(storageKey);
                const newUserId = response.User?.ID;
                const newUserToken = response.User?.AuthenticationToken || response.User.AuthenticationToken;
                if (storedCurrentOrderValue && newUserId && newUserToken) {
                    const newStorageKey = `${evaEndpointUrl.replace(/https?:\/\//, '')}:${newUserId}:shoppingCartDetails`;
                    storage.setItem(newStorageKey, storedCurrentOrderValue);
                    setCurrentUserToken(newUserToken);
                }
            }
            // Check if the modify response has returned a new order id
            // Not all services will return a new OrderID
            //
            const newOrderId = response.OrderID;
            if (newOrderId && newOrderId !== orderId) {
                // Set the new current shopping cart
                //
                setShoppingCartRequest({
                    ...shoppingCartRequest,
                    OrderID: newOrderId,
                });
            }
            else if (hookCallOptions?.doNotSetStale !== true) {
                // Mark the existing shopping cart as being stale
                // This can be disabled for performing multiple cart modifying actions in bulk
                //
                setShoppingCartStale(`${new Date().valueOf()}`);
            }
            if (response && options.onSuccess) {
                options.onSuccess(response);
            }
            return response;
        }
        catch (error) {
            logger.error(`Failed to call service: ${service.name}`, error)();
            if (options.onError) {
                options.onError(error);
            }
        }
    }, [
        evaEndpointUrl,
        currentUserToken,
        evaRequestedOrganizationUnitID,
        evaRequestedOrganizationUnitQuery,
        service,
        shoppingCart,
        shoppingCartRequest,
    ]);
    return modifyCart;
};
export default useShoppingCartModify;
//# sourceMappingURL=use-shopping-cart-modify.js.map